import { apiClient as api } from "./httpClient";

/**
 * Ocupação dos profissionais (RN-225): grade de disponibilidade × agenda no mês escolhido e no seguinte.
 * Horas em decimal (a conta é em minutos: sessão de 30 min ocupa 0,5h). Só consulta.
 */
export interface CapacityTotals {
  totalHours: number;
  occupiedHours: number;
  availableHours: number;
  occupiedPercent: number;
  outsideGridHours: number;
}

export interface CapacityWeekPoint {
  weekStart: string;
  nextMonth: boolean;
  totalHours: number;
  occupiedHours: number;
  occupiedPercent: number;
}

/** Faixa de 30 min; weekday 0=dom … 6=sáb; slotStart em minutos desde a meia-noite. */
export interface CapacityHeatCell {
  weekday: number;
  slotStart: number;
  capacityMinutes: number;
  occupiedMinutes: number;
  outsideMinutes: number;
  occupiedPercent: number;
  endingSoon: boolean;
}

export interface CapacityEndingLink {
  patientId: string;
  patientName: string;
  serviceName: string | null;
  weekday: number;
  startTime: string;
  endTime: string;
  lastDate: string;
  situation: string;
  occurrence: string | null;
}

export interface ProfessionalCapacityRow {
  professionalId: string;
  name: string;
  specialty: string;
  hasGrid: boolean;
  current: CapacityTotals;
  next: CapacityTotals;
  weeks: CapacityWeekPoint[];
  heatCurrent: CapacityHeatCell[];
  heatNext: CapacityHeatCell[];
  endingLinks: CapacityEndingLink[];
}

export interface SpecialtyCapacityRow {
  specialty: string;
  professionals: number;
  current: CapacityTotals;
  next: CapacityTotals;
}

export interface CapacityOption {
  id: string;
  name: string;
  specialty: string;
}

export interface ProfessionalCapacityReport {
  month: string;
  currentFrom: string;
  currentTo: string;
  nextFrom: string;
  nextTo: string;
  today: string;
  endingPastDays: number;
  endingAheadDays: number;
  slotMinutes: number;
  firstMinute: number;
  lastMinute: number;
  current: CapacityTotals;
  next: CapacityTotals;
  bySpecialty: SpecialtyCapacityRow[];
  professionals: ProfessionalCapacityRow[];
  professionalOptions: CapacityOption[];
  specialtyOptions: string[];
}

export interface ProfessionalCapacityFilters {
  month: string;
  professionals: string[];
  specialty: string;
  /** bloqueio de agenda (RN-118n) sai das horas disponíveis; desligado, vale a grade cadastrada */
  considerBlocks: boolean;
}

export const professionalCapacityService = {
  get: async (filters: ProfessionalCapacityFilters): Promise<ProfessionalCapacityReport> => {
    const params = new URLSearchParams();
    if (filters.month) params.append("month", filters.month);
    if (filters.professionals.length > 0) params.append("professionals", filters.professionals.join(","));
    if (filters.specialty) params.append("specialty", filters.specialty);
    if (!filters.considerBlocks) params.append("considerBlocks", "false");
    const response = await api.get(`/reports/professional-capacity?${params.toString()}`, { timeout: 90000 });
    return response.data;
  },
};
